import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Button } from '@/components/Button/Button';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import {
  addWizardSchema,
  type AddMemberRequest,
  type AddWizardValues,
  type Member,
  type StaffProfileDetail,
} from '../schemas';
import { getStaffProfileDetail } from '../api';
import { useActiveTenantId, useAddMember } from '../hooks';
import { useAuthStore } from '@/stores/auth';
import { Stepper } from '@/components/Stepper/Stepper';
import {
  WIZARD_STEPS,
  TERMINAL_BACK,
  isTerminalId,
  type TerminalId,
  type WizardStepId,
} from './hire-wizard/steps';
import { AddMemberSuccess } from './hire-wizard/HireSuccess';
import { HumanComingSoon } from './hire-wizard/HumanComingSoon';
import { HireHumanForm } from './hire-wizard/HireHumanForm';
import styles from './HireWizard.module.css';

export function AddMemberWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as {
    preselectStaffProfileId?: string;
    skipType?: boolean;
  } | null;
  const preselectId = navState?.preselectStaffProfileId ?? '';
  const skipType = navState?.skipType ?? false;

  const [currentIdx, setCurrentIdx] = useState(0);
  const [terminal, setTerminal] = useState<TerminalId | null>(null);
  const [visitedSteps, setVisitedSteps] = useState<Set<WizardStepId>>(new Set(['type']));
  const [addedMember, setHiredMember] = useState<Member | null>(null);
  // profileDetail is consumed by StepBrief (Task 11) and Reset-to-defaults.
  // Task 9 wires setProfileDetail inside prefillFromStaffProfile.
  const [profileDetail, setProfileDetail] = useState<StaffProfileDetail | null>(null);

  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();
  const add = useAddMember();
  const canAddHumans = useAuthStore((s) => s.canAddHumans);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<AddWizardValues>({
    resolver: zodResolver(addWizardSchema),
    defaultValues: {
      memberKind: 'ai',
      description: '',
      staffProfileId: preselectId,
      roleSkipped: false,
      first_name: '',
      last_name: '',
      positionId: '',
      unitId: '',
      personality: '',
      systemPrompt: '',
    },
    mode: 'onChange',
  });

  const values = form.watch();
  const currentStep = WIZARD_STEPS[currentIdx]!;

  const prefillFromStaffProfile = useCallback(
    async (staffProfileId: string) => {
      const detail = await queryClient.fetchQuery({
        queryKey: ['staff-profiles', tenantId, 'detail', staffProfileId] as const,
        queryFn: () => getStaffProfileDetail(tenantId, staffProfileId),
      });
      const item = detail.item;
      form.setValue('first_name', item.first_name);
      form.setValue('last_name', item.last_name ?? '');
      // Hire form is single-select per slot; pre-fill with the first entry
      // (sorted alphabetically server-side) and let the user pick another.
      form.setValue('positionId', item.defaultPositions[0]?.id ?? '');
      form.setValue('unitId', item.defaultUnits[0]?.id ?? '');
      form.setValue('personality', item.personality ?? '');
      form.setValue('systemPrompt', item.systemPrompt);
      // profileDetail cached here for Reset-to-defaults consumer (StepBrief).
      setProfileDetail(item);
    },
    [queryClient, tenantId, form],
  );

  // Pre-selection from Library: jump to Identity with profile prefilled.
  useEffect(() => {
    if (!preselectId) return;
    let cancelled = false;
    void (async () => {
      await prefillFromStaffProfile(preselectId);
      if (cancelled) return;
      form.setValue('staffProfileId', preselectId);
      setVisitedSteps(new Set(['type', 'role', 'identity']));
      setCurrentIdx(WIZARD_STEPS.findIndex((s) => s.id === 'identity'));
    })();
    return () => {
      cancelled = true;
    };
    // Only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Hire an AI Member" entry points (dashboard Getting Started, etc.) skip
  // the Type step entirely. hireKind already defaults to 'ai'; we just mark
  // Type as visited and jump to Describe the role. Preselection wins if both
  // flags are set (the preselect effect above runs from idx=Identity).
  useEffect(() => {
    if (!skipType || preselectId) return;
    setVisitedSteps(new Set(['type', 'role']));
    setCurrentIdx(WIZARD_STEPS.findIndex((s) => s.id === 'role'));
    // Only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = useCallback(async () => {
    if (currentStep.onAdvance) {
      try {
        await currentStep.onAdvance(values, { prefillFromStaffProfile });
      } catch (err) {
        console.error('[HireWizard] onAdvance failed', err);
        return;
      }
    }
    const nextId = currentStep.nextId?.(values);
    if (nextId && isTerminalId(nextId)) {
      setTerminal(nextId);
      return;
    }
    const nextIdx = nextId ? WIZARD_STEPS.findIndex((s) => s.id === nextId) : currentIdx + 1;
    if (nextIdx >= 0 && nextIdx < WIZARD_STEPS.length) {
      setVisitedSteps((prev) => new Set(prev).add(WIZARD_STEPS[nextIdx]!.id));
      setCurrentIdx(nextIdx);
    } else if (currentStep.id === 'confirm') {
      setFormError(null);
      const body: AddMemberRequest = {};
      if (values.roleSkipped) {
        // Custom-from-scratch hire — no profile to diff against; send the
        // manual fields verbatim. first_name + systemPrompt are guaranteed
        // non-empty because StepBrief's canContinue enforces it.
        body.first_name = values.first_name;
        if (values.last_name) body.last_name = values.last_name;
        body.personality = values.personality;
        body.systemPrompt = values.systemPrompt;
      } else {
        body.staffProfileId = values.staffProfileId;
        if (values.first_name) body.first_name = values.first_name;
        if (values.last_name) body.last_name = values.last_name;
        if (profileDetail) {
          if ((values.personality ?? '') !== (profileDetail.personality ?? '')) {
            body.personality = values.personality;
          }
          if (values.systemPrompt !== profileDetail.systemPrompt) {
            body.systemPrompt = values.systemPrompt;
          }
        }
      }
      if (values.positionId) body.positionIds = [values.positionId];
      if (values.unitId) body.unitIds = [values.unitId];
      add.mutate(body, {
        onSuccess: (res) => {
          setHiredMember(res.member);
          setTerminal('success');
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'AGENT_TEMPLATE_NOT_FOUND') {
              setFormError(t('That Staff Profile is no longer available.'));
              setCurrentIdx(WIZARD_STEPS.findIndex((s) => s.id === 'role'));
            } else if (err.status === 403) {
              setFormError(t("You don't have permission to add Members."));
            } else {
              setFormError(err.message);
            }
          } else if (err instanceof NetworkError) {
            setFormError(t("Couldn't reach the server. Check your connection and try again."));
          } else {
            setFormError(t('Something went wrong. Please try again.'));
          }
        },
      });
    }
  }, [currentStep, currentIdx, values, prefillFromStaffProfile, profileDetail, add]);

  const handleBack = useCallback(() => {
    if (terminal) {
      const back = TERMINAL_BACK[terminal];
      setTerminal(null);
      if (back) setCurrentIdx(WIZARD_STEPS.findIndex((s) => s.id === back));
      return;
    }
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  }, [terminal, currentIdx]);

  const handleCancel = useCallback(() => {
    void navigate('/team');
  }, [navigate]);

  const handleJumpBack = useCallback(
    (idx: number) => {
      if (idx < currentIdx) setCurrentIdx(idx);
    },
    [currentIdx],
  );

  const continueDisabled = !currentStep.canContinue(values);
  const continueLabel = currentStep.continueLabel?.(values) ?? t('Continue');

  if (terminal === 'success' && addedMember) {
    return (
      <div className={styles.page}>
        <AddMemberSuccess member={addedMember} />
      </div>
    );
  }

  if (terminal === 'human-coming-soon') {
    return (
      <div className={styles.page}>
        <HumanComingSoon
          onBack={handleBack}
          onBackToTeam={() => void navigate('/team')}
          canAddHumans={canAddHumans}
          onDirectHire={() => setTerminal('add-human')}
        />
      </div>
    );
  }

  if (terminal === 'add-human') {
    return (
      <div className={styles.page}>
        <HireHumanForm
          onSuccess={(member) => {
            setHiredMember(member);
            setTerminal('success');
          }}
          onCancel={() => setTerminal('human-coming-soon')}
        />
      </div>
    );
  }

  const StepComponent = currentStep.Component as React.ComponentType<{
    profileDetail?: StaffProfileDetail | null;
    formError?: string | null;
  }>;

  return (
    <div className={styles.page}>
      <Stepper
        steps={WIZARD_STEPS}
        currentIdx={currentIdx}
        visited={visitedSteps}
        onJumpBack={handleJumpBack}
      />
      <FormProvider {...form}>
        <StepComponent profileDetail={profileDetail} formError={formError} />
      </FormProvider>
      <div className={styles.footer}>
        {currentIdx === 0 ? (
          <Button type="button" variant="ghost" size="md" onClick={handleCancel}>
            {t('Cancel')}
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="md" onClick={handleBack}>
            {t('← Back')}
          </Button>
        )}
        <div className={styles.rightGroup}>
          <span className={styles.stepCount}>
            {t('Step {n} of {total}', { n: currentIdx + 1, total: WIZARD_STEPS.length })}
          </span>
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={continueDisabled || add.isPending}
            isLoading={currentStep.id === 'confirm' && add.isPending}
            onClick={() => void handleContinue()}
          >
            {continueLabel} →
          </Button>
        </div>
      </div>
    </div>
  );
}
